import { useEffect, useCallback } from 'react';

/**
 * Hook that calls the provided callback when the Escape key is pressed.
 * Used for closing modals, dropdowns, and mobile navigation.
 *
 * @param onEscape - Callback function to execute when Escape is pressed
 * @param enabled - Whether the hook is active (default: true)
 *
 * @example
 * ```tsx
 * useEscapeKey(() => setSidebarOpen(false), sidebarOpen);
 * ```
 */
export function useEscapeKey(onEscape: () => void, enabled = true): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape();
      }
    },
    [onEscape]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}

export default useEscapeKey;
